<script setup lang="ts">
const colors = useColor()
const { data: user, refresh, premiumExpiredAt } = useAuth()
const maxExp = computed(
  () =>
    levelSection.find(
      (item) => item.level_id === (user.value?.user.level_id || 0) + 1
    )?.experience || 1
)
const percentage = computed(() => {
  return Number((((user.value?.user.exp || 0) / maxExp.value) * 100).toFixed(2))
})

const levelSection = [
  {
    id: 10,
    experience: 50000,
    level_id: 10,
    badges_id: 10
  },
  {
    id: 9,
    experience: 16000,
    level_id: 9,
    badges_id: 9
  },
  {
    id: 8,
    experience: 7500,
    level_id: 8,
    badges_id: 8
  },
  {
    id: 7,
    experience: 4000,
    level_id: 7,
    badges_id: 7
  },
  {
    id: 6,
    experience: 1800,
    level_id: 6,
    badges_id: 6
  },
  {
    id: 5,
    experience: 900,
    level_id: 5,
    badges_id: 5
  },
  {
    id: 4,
    experience: 500,
    level_id: 4,
    badges_id: 4
  },
  {
    id: 3,
    experience: 200,
    level_id: 3,
    badges_id: 3
  },
  {
    id: 2,
    experience: 50,
    level_id: 2,
    badges_id: 2
  },
  {
    id: 1,
    experience: 1,
    level_id: 1,
    badges_id: 1
  }
]

const openUserCenter = (path = '') => {
  window.open(`/member/${user.value?.user.id}${path}`, '_blank')
}

const openPremium = () => {
  window.open(`/member/${user.value?.user.id}/premium`, '_blank')
}

const logout = async () => {
  await requestSignOut()
  await refresh()
}
</script>

<template>
  <div class="m-user-card">
    <div class="m-user-card__header">
      <UserAvatar
        size="md"
        :src="user.user.avatar"
        :auth="user.user.user_auth"
        class="pointer"
        @click="openUserCenter()"
      ></UserAvatar>
      <div class="m-user-card__info">
        <div class="m-user-card__name">
          <m-text
            size="md"
            bold
            class="pointer"
            :style="{ color: namecolorMap[user.user.name_color] ?? null }"
            @click="openUserCenter()"
          >
            {{ user.user.name }}
          </m-text>
          <MBadge :badges="[user.user.level_badge, ...user.user.badges]" />
        </div>
        <n-progress
          type="line"
          :height="4"
          :color="colors.primary"
          :border-radius="0"
          :percentage="percentage"
          :show-indicator="false"
        />
        <div class="m-user-card__exp">
          <m-text type="secondary" size="sm">UID:{{ user.user.id }}</m-text>
          <m-text type="secondary" size="sm"
            >{{ user.user.exp }}/{{ maxExp }}
          </m-text>
        </div>
      </div>
    </div>
    <hr v-if="premiumExpiredAt" />
    <div
      v-if="premiumExpiredAt"
      class="m-user-card__premium"
      @click="openPremium()"
    >
      <MBadge :badges="[20]" />
      <m-text size="sm">会员到期时间</m-text>
      <m-text size="sm">{{ pipe.exactDate(premiumExpiredAt) }}</m-text>
    </div>
    <hr />
    <div class="m-user-card__fans">
      <div class="count" @click="openUserCenter('/fans')">
        <m-text size="sm" bold>{{ user.user.follow.fans }}</m-text>
        <m-text size="sm" type="secondary">粉丝</m-text>
      </div>
      <div class="count" align="center" @click="openUserCenter('/follow')">
        <m-text size="sm" bold>{{ user.user.follow.follow }}</m-text>
        <m-text size="sm" type="secondary">关注</m-text>
      </div>
      <div class="count" align="center">
        <m-text size="sm" bold>{{ user.user.neko_coin }}</m-text>
        <m-text size="sm" type="secondary">喵币</m-text>
      </div>
    </div>
    <hr />
    <div class="m-user-card__action" @click="openUserCenter()">
      <div class="i-lucide:user-round myz-text--lg"></div>
      <div>
        <m-text>个人中心</m-text>
      </div>
      <div class="icon i-lucide:chevron-right w-1em h-1em"></div>
    </div>
    <div class="m-user-card__action" @click="logout">
      <div class="i-lucide:log-out w-1em h-1em"></div>

      <div>
        <m-text>退出登录</m-text>
      </div>
      <div class="icon i-lucide:chevron-right w-1em h-1em"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.m-user-card {
  width: 270px;
  padding: 12px 6px 16px 6px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  &__name {
    display: flex;
    flex-direction: row;
    gap: 4px;
  }
  &__header {
    display: flex;
    gap: 12px;
  }
  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__exp {
    display: flex;
    justify-content: space-between;
  }
  &__premium {
    padding: 8px 8px;
    border-radius: 6px;
    cursor: pointer;
    background-color: var(--color-red-100);
    display: flex;
    justify-content: space-between;
    align-items: center;
    > * {
      height: 20px;
    }
  }
  &__fans {
    display: flex;
    justify-content: space-between;
    .count {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      &:hover * {
        color: var(--color-primary);
      }
    }
  }
  &__action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    border-radius: 6px;
    padding: 4px 0;
    cursor: pointer;
    .icon {
      margin-left: auto;
    }
    &.premium {
      background-color: var(--color-red-100);
      color: white;
    }
  }
}
</style>
